// Face-PRESENT gate for auto-capture (plan §2.2, D10). Detection only: it reads a box and a
// confidence per face and never computes, stores or compares a face template.
//
// Rule: exactly one face with confidence > 0.6 whose box is taller than 20% of the frame, held
// for 500 ms. Height rather than area: an adult signing at a door tablet is 40-70 cm from the
// lens, where a face fills about a quarter of the frame's height but well under 20% of its area.

export interface Detection { conf: number; x: number; y: number; w: number; h: number }

export interface GateOptions { minConf: number; minHeight: number; holdMs: number }
export const GATE: GateOptions = { minConf: 0.6, minHeight: 0.2, holdMs: 500 };

export type GateStatus = "none" | "many" | "small" | "holding" | "ready";

export interface GateState { since: number | null; best: Detection | null; status: GateStatus }
export const gateStart: GateState = { since: null, best: null, status: "none" };

/** Pure step function: feed it each detector result with the frame height and a monotonic time. */
export function gateStep(s: GateState, dets: readonly Detection[], frameH: number, now: number, o: GateOptions = GATE): GateState {
  const confident = dets.filter((d) => d.conf > o.minConf);
  if (confident.length === 0) return { since: null, best: null, status: "none" };
  if (confident.length > 1) return { since: null, best: null, status: "many" };
  const d = confident[0]!;
  if (frameH <= 0 || d.h / frameH <= o.minHeight) return { since: null, best: d, status: "small" };
  const since = s.since ?? now;
  return { since, best: d, status: now - since >= o.holdMs ? "ready" : "holding" };
}

/** Converts a detection in video pixels into the evidence format in photo pixels (events spec §2). */
export function faceRecord(d: Detection, scale: number, photoW: number, photoH: number) {
  const clamp = (v: number, max: number) => Math.max(0, Math.min(max, Math.round(v)));
  const x = clamp(d.x * scale, photoW);
  const y = clamp(d.y * scale, photoH);
  return {
    conf: Math.max(0, Math.min(1000, Math.round(d.conf * 1000))),
    box: [x, y, clamp(d.w * scale, photoW - x), clamp(d.h * scale, photoH - y)] as [number, number, number, number],
  };
}

export const GATE_HINT: Record<GateStatus, string> = {
  none: "Look at the camera",
  many: "One person, please",
  small: "Step a little closer",
  holding: "Hold still",
  ready: "Photo taken",
};
